import React, { useState, Fragment, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Table,
  TableContainer,
  Text,
  useToken,
  Flex,
  Checkbox,
  CheckboxGroup,
  Stack,
  Button,
  Heading,
  useToast,
} from "@chakra-ui/react";
import TableHead from "../Campaigns/components/TableHead";
import OrderTable from "./OrderTable";
import OrderStats from "./OrderStats";
import { exportCSV, getOrders, savePostOrderSettings, getPostOrderSettings } from "../../Services/api";
import { useQuery } from "react-query";
import { abbreviateNumber } from "js-abbreviation-number";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import * as _ from "lodash";
import ExportCSV from "./ExportCSV";
import Pagination from "../../components/Pagination";

const Orders = () => {
  const [orderData, setOrderData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [email, setEmail] = useState("");
  const [mainColor] = useToken("colors", ["primary.main"]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get("view") || "orders";

  const setView = (newView) => {
    if (newView === "orders") {
      setSearchParams({});
    } else {
      setSearchParams({ view: newView });
    }
  };

  const [selectedJourneys, setSelectedJourneys] = useState([]);
  const toast = useToast();

  const selectedOrgData = localStorage.getItem("selectedOrg");
  const selectedOrg = JSON.parse(selectedOrgData);
  const orgId = selectedOrg.token;
  const OPTIONS = [
  { label: "Order Confirmation", value: "ORDER_CONFIRMATION" },
  { label: "Address Confirmation", value: "ADDRESS_CONFIRMATION" },
  { label: "Order Cancellation", value: "ORDER_CANCELLATION" },
];


  const fetchOrderData = useQuery(
    ["orders", orgId, currentPage],
    async () => {
      const res = await getOrders(orgId, currentPage);
      setTotalCount(res.data?.data?.order_aggregate[0]._total_count ?? 0);
      return res.data.data;
    },
    {
      staleTime: 0,
      retry: 1,
      enabled: !!orgId,
    }
  );

  useEffect(() => {
  const fetchSettings = async () => {
    try {
      if (view !== "settings") return;

      const res = await getPostOrderSettings();

      const journeys = res?.data?.journeys;

      if (Array.isArray(journeys)) {
        setSelectedJourneys(journeys);
      } else {
        setSelectedJourneys([]); // fallback → all unchecked
      }
    } catch (err) {
      console.error("Error fetching settings", err);
      setSelectedJourneys([]);
    }
  };

  fetchSettings();
}, [view, orgId]);


  let totalGrossRevenue = "";
  if (fetchOrderData.data && Array.isArray(fetchOrderData.data.order_sum)) {
    totalGrossRevenue = abbreviateNumber(
      _.sum(fetchOrderData.data.order_sum.map((e) => e.total_gross_revenue))
    );
  }

  let totalDiscounts = "";
  if (fetchOrderData.data && Array.isArray(fetchOrderData.data.order_sum)) {
    totalDiscounts = abbreviateNumber(
      _.sum(fetchOrderData.data.order_sum.map((e) => e.total_discounts))
    );
  }

  let totalReturns = "";
  if (fetchOrderData.data && Array.isArray(fetchOrderData.data.order_sum)) {
    totalReturns = abbreviateNumber(
      _.sum(fetchOrderData.data.order_sum.map((e) => e.total_refunded))
    );
  }

  const orderStatsData = {
    Orders: fetchOrderData.data?.order_aggregate[0]._total_count ?? 0,
    "Gross Revenue": totalGrossRevenue,
    "Total Discounts": totalDiscounts,
    Returns: totalReturns,
  };

  const downloadcsv = async (orgId, email) => {
    try {
      const data = await exportCSV(orgId, email);
      console.log("CSV export data:", data);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  // ✅ SAVE SETTINGS HANDLER
  const handleSaveSettings = async () => {
  try {
    const payload = {
      orgId,
      journeys: Array.isArray(selectedJourneys)
        ? selectedJourneys
        : [selectedJourneys],
    };

    await savePostOrderSettings(payload);

    toast({
      title: "Settings saved",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (err) {
    toast({
      title: "Error saving settings",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  }
};

  return (
    <Fragment>
      <Box>
        {/* ✅ HEADER BUTTONS */}
        <Flex justifyContent="end" gap={3}>
          {view === "orders" ? (
            <>
              <PrimaryButton
                onClick={() => setView("settings")}
                label="Settings"
              />

              <PrimaryButton
                onClick={() => setShowAlert(true)}
                label="Export CSV"
              />
            </>
          ) : (
            <PrimaryButton
              onClick={() => setView("orders")}
              label="Back to Orders"
            />
          )}
        </Flex>

        <ExportCSV
          isOpen={showAlert}
          title="Export CSV"
          text="Export orders to CSV"
          confirmButtonText="Export"
          cancelButtonText="Cancel"
          onCancel={() => setShowAlert(false)}
          downloadcsv={downloadcsv}
        />
      </Box>

      {/* ✅ MAIN CONTENT SWITCH */}
      {view === "orders" ? (
        <>
          <OrderStats
            orderData={orderStatsData}
            loading={fetchOrderData.isLoading}
          />

          <TableContainer bg="white" maxH="60vh" w="99%" overflowY="auto" mt={6}>
            <Table>
              <TableHead
                headersProps={[
                  "Order Id",
                  "Email",
                  "Order Date",
                  "Gross Revenue",
                  "Shipping Cost",
                  "Discount",
                  "Gross Profit",
                  "Gross Margin",
                  "State",
                  "Data Source",
                  "Channel",
                  "Campaign",
                  "Source",
                  "Device",
                ]}
              />
              <OrderTable
                orderData={fetchOrderData}
                loading={fetchOrderData.isLoading}
              />
            </Table>
          </TableContainer>

          {!fetchOrderData.isLoading && totalCount > 0 && (
            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              isDisabled={fetchOrderData.isLoading}
            />
          )}
        </>
      ) : (
        <Box mt={6} bg="white" p={6} borderRadius="md">
          <Heading size="md" mb={4}>
            Select the post-order journeys you want to enable:
          </Heading>

          <CheckboxGroup
  value={selectedJourneys}
  onChange={setSelectedJourneys}
>
  <Stack spacing={3}>
    {OPTIONS.map((opt) => (
      <Checkbox key={opt.value} value={opt.value}>
        {opt.label}
      </Checkbox>
    ))}
  </Stack>
</CheckboxGroup>


          <Button mt={6} colorScheme="blue" onClick={handleSaveSettings}>
            Save
          </Button>
        </Box>
      )}
    </Fragment>
  );
};

export default Orders;
